/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { minimumOrderQuantity } from '../lib/orderQuantity';

const CART_STORAGE_KEY = 'udyam_cart';
const CartContext = createContext(null);

function readCart() {
    try {
        const parsed = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || '[]');
        return Array.isArray(parsed) ? parsed : [];
    }
    catch {
        return [];
    }
}

export function CartProvider({ children }) {
    const [items, setItems] = useState(readCart);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        try { localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items)); }
        catch { /* storage unavailable — no-op */ }
    }, [items]);

    const addToCart = useCallback((product, qty = 1) => {
        setItems((prev) => {
            const existing = prev.find((it) => it.id === product.id);
            if (existing) {
                return prev.map((it) => it.id === product.id ? { ...it, qty: Math.max(minimumOrderQuantity(product), it.qty + qty) } : it);
            }
            return [...prev, {
                id: product.id,
                name: product.name,
                image: product.image,
                price: product.price,
                supplierId: product.supplierId,
                supplierName: product.supplierName,
                minOrderQty: product.minOrderQty,
                qty: Math.max(minimumOrderQuantity(product), qty),
            }];
        });
    }, []);

    const removeFromCart = useCallback((id) => {
        setItems((prev) => prev.filter((it) => it.id !== id));
    }, []);

    const updateQuantity = useCallback((id, qty) => {
        setItems((prev) => prev.map((it) => it.id === id ? { ...it, qty: Math.max(minimumOrderQuantity(it), qty) } : it));
    }, []);

    const clearCart = useCallback(() => setItems([]), []);
    const openCart = useCallback(() => setIsOpen(true), []);
    const closeCart = useCallback(() => setIsOpen(false), []);

    const cartCount = items.reduce((sum, it) => sum + it.qty, 0);

    return (<CartContext.Provider value={{ items, addToCart, removeFromCart, updateQuantity, clearCart, cartCount, isOpen, openCart, closeCart }}>
      {children}
    </CartContext.Provider>);
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx)
        throw new Error('useCart must be used within a CartProvider');
    return ctx;
}
